import type {SharedData, UnitSystem} from '@gaia/types';
import getTransformRequest from '@gaia/utils/gaia/getTransformRequest';
import concatMapboxStyles from '@gaia/utils/mapbox/concatMapboxStyles';
import dedupeMapboxStyleSources from '@gaia/utils/mapbox/dedupeMapboxStyleSources';
import surfaceMapboxStylesLabels from '@gaia/utils/mapbox/surfaceMapboxStylesLabels';
import transformMapboxStyleFonts from '@gaia/utils/mapbox/transformMapboxStyleFonts';
import type {GetSyncPromise, SyncPromise} from '@gaia/utils/react/useGetSyncPromise';
import unzip from 'lodash/unzip';
import type mapboxgl from 'mapbox-gl';

import fetchLayerMapboxStyle from './fetchLayerMapboxStyle';
import get3DMapboxStyle from './get3DMapboxStyle';
import getAreasMapboxStyle from './getAreasMapboxStyle';
import getDemoPositionMapboxStyle from './getDemoPositionMapboxStyle';
import getGridMapboxStyle from './getGridMapboxStyle';
import getKnownRoutesMapboxStyle from './getKnownRoutesMapboxStyle';
import getMarkedLocationMapboxStyle from './getMarkedLocationMapboxStyle';
import getPublicTracksMapboxStyle from './getPublicTracksMapboxStyle';
import getSelectionsMapboxStyle from './getSelectionsMapboxStyle';
import getSharedDataGeometryMapboxStyle from './getSharedDataGeometryMapboxStyle';
import {getTracksMapboxStyle} from './getTracksMapboxStyle';
import getWaypointsMapboxStyle from './getWaypointsMapboxStyle';

interface GetSyncMapboxStyleOptions {
    areas: {[id: string]: Area};
    bounds?: mapboxgl.LngLatBounds;
    getStyle: MapGetStyle;
    grid: Grid;
    sharedData: SharedData;
    knownRoutes: Record<number, KnownRouteTag>;
    interaction: InteractionState;
    is3D: boolean;
    isPrinting: boolean;
    layers?: Layer[];
    layerTypes: {[key: string]: LayerType} | undefined;
    preview?: boolean;
    isEmbedded?: boolean;
    project: MapProject;
    querySourceFeatures: MapQuerySourceFeatures;
    selections: SelectionState[];
    showAreas: boolean;
    showPublicTracks: boolean;
    showTracks: boolean;
    showRoutes: boolean;
    showWaypointLabels?: boolean;
    showWaypoints: boolean;
    showKnownRoutes: boolean;
    showArchived: boolean;
    tracks: {[id: string]: Track};
    waypoints: {[id: string]: Waypoint};
    zoom?: number;
    getSyncPromise: GetSyncPromise;
    visibilities: Record<string, boolean>;
    unitSystem: UnitSystem;
    isAuthenticated: boolean;
    activeFolderIds: string[] | undefined;
}

interface GetSyncMapboxStyleOutput {
    mapboxStyle: GaiaMapboxStyle;
    transformRequest: mapboxgl.TransformRequestFunction;
}

export default function getSyncMapboxStyle({
    areas,
    bounds,
    grid,
    sharedData,
    getStyle,
    knownRoutes,
    interaction,
    is3D,
    isPrinting,
    layers = [],
    layerTypes,
    preview,
    isEmbedded,
    project,
    querySourceFeatures,
    selections,
    showPublicTracks,
    showAreas,
    showTracks,
    showRoutes,
    showWaypointLabels,
    showWaypoints,
    showKnownRoutes,
    showArchived,
    tracks,
    waypoints,
    getSyncPromise,
    zoom,
    visibilities,
    unitSystem,
    isAuthenticated,
    activeFolderIds,
}: GetSyncMapboxStyleOptions): SyncPromise<GetSyncMapboxStyleOutput> {
    // "getSyncMapboxStyle" calls submodules, each of which is responsible for some subset of
    // functionality, and each of which returns a complete Mapbox style.

    const selectedOverlays = [
        showAreas && 'areas',
        showTracks && 'tracks',
        showRoutes && 'routes',
        (showWaypoints || showWaypointLabels) && 'waypoints',
    ]
        .filter(Boolean)
        .join('|');

    const userSource: mapboxgl.VectorSource | undefined = selectedOverlays
        ? {
              type: 'vector',
              tiles: [`${window.location.origin}/api/tiles/${selectedOverlays}/{z}/{x}/{y}.mvt`],
              minzoom: 3,
              maxzoom: 14,
          }
        : undefined;

    const [styles, errors, isLoaded] = (!layerTypes
        ? [[], [], []]
        : unzip(
              [
                  getDemoPositionMapboxStyle(layerTypes, layers),
                  fetchLayerMapboxStyle(
                      layerTypes,
                      {
                          opacity: 1,
                          type:
                              unitSystem === 'metric'
                                  ? 'GaiaTopoRasterMeters'
                                  : 'GaiaTopoRasterFeet',
                      },
                      {preview, id: 'basemap'}
                  ),
                  ...layers.map((layer: Layer) =>
                      fetchLayerMapboxStyle(layerTypes, layer, {preview})
                  ),
                  get3DMapboxStyle(is3D),
                  getGridMapboxStyle(grid, bounds, zoom, is3D),
                  getPublicTracksMapboxStyle({
                      showPublicTracks,
                      isPrinting,
                      selections,
                      interaction,
                  }),
                  getTracksMapboxStyle({
                      showTracks,
                      showRoutes,
                      tracks,
                      project,
                      interaction,
                      getSyncPromise,
                      sharedData,
                      isPrinting,
                      isAuthenticated,
                      selections,
                      visibilities,
                      showArchived,
                      userSource,
                      activeFolderIds,
                  }),
                  getWaypointsMapboxStyle({
                      showWaypoints,
                      showWaypointLabels,
                      waypoints,
                      interaction,
                      sharedData,
                      isPrinting,
                      tracks,
                      isAuthenticated,
                      selections,
                      visibilities,
                      showArchived,
                      userSource,
                      activeFolderIds,
                  }),
                  getAreasMapboxStyle({
                      showAreas,
                      areas,
                      interaction,
                      sharedData,
                      isPrinting,
                      selections,
                      visibilities,
                      showArchived,
                      userSource,
                      activeFolderIds,
                      unitSystem,
                      isAuthenticated,
                  }),
                  getSharedDataGeometryMapboxStyle(sharedData), // Shared data geometry includes shareable links and embeds
                  ...selections.map((s) =>
                      getSelectionsMapboxStyle(getStyle, querySourceFeatures, s)
                  ),
                  getKnownRoutesMapboxStyle({
                      showKnownRoutes,
                      knownRoutes,
                      selections,
                      interactionType: interaction.type,
                  }),
                  getMarkedLocationMapboxStyle({
                      interaction,
                      preview,
                      isEmbedded,
                      selections,
                  }),
              ]
                  .filter(Boolean)
                  .map((promise) => getSyncPromise(promise))
          )) as any as [(GaiaMapboxStyle | undefined)[], (Error | undefined)[], boolean[]];

    // Transform the styles to allow for merging of different glpyh (font) endpoints
    const transformedStyles = prefixMapboxStyleFonts(
        styles.filter((s): s is GaiaMapboxStyle => !!s)
    );
    const transformRequest = getTransformRequest(styles.filter((s): s is GaiaMapboxStyle => !!s));

    const mapboxStyle = {
        // Merge duplicate Mapbox sources to prevent redundant network requests
        ...dedupeMapboxStyleSources(
            // Merge array of styles from submodules into one style
            concatMapboxStyles(
                // Move "symbol" layers to the top of the style
                surfaceMapboxStylesLabels(transformedStyles)
            )
        ),

        glyphs: 'g://{fontstack}/{range}',
    };

    const defaultOutput = {
        mapboxStyle: {version: 8, layers: [], sources: {}},
        transformRequest,
    };

    return !layerTypes
        ? [defaultOutput, undefined, false]
        : ([
              {mapboxStyle, transformRequest},
              errors.find((e) => !!e),
              !!(isLoaded.every((v) => !!v) && layerTypes),
          ] as SyncPromise<GetSyncMapboxStyleOutput>);
}

// We intend to merge together a number of Mapbox styles. Each Mapbox style may specify a different
// `glyphs` endpoint, which defines available fonts. But the final merged style may only specify
// one `glyphs` endpoint. Our workaround is to munge the font names into the format
// `${style id}__${font name}` and then use `transformRequest` to dispatch glyph requests to the
// correct URL.
function prefixMapboxStyleFonts(styles: GaiaMapboxStyle[]) {
    return styles.map((style) => {
        function fontVisitor(font: string[]) {
            if (!style!.metadata.id.includes('selectionStyle')) {
                return [`${style!.metadata.id}__${font.join(',')}`];
            } else {
                return [font.join(',')];
            }
        }

        // Mapbox GL does not provide any expressions for manipulating arrays so we must traverse
        // the expression looking for literals that look like font stacks.
        function fontExpressionVisitor(expression: mapboxgl.Expression): mapboxgl.Expression {
            if (!Array.isArray(expression)) {
                return expression;
            } else if (
                expression[0] === 'literal' &&
                Array.isArray(expression[1]) &&
                expression[1].every((v) => typeof v === 'string')
            ) {
                return ['literal', fontVisitor(expression[1])];
            } else {
                return [expression[0], ...expression.slice(1).map(fontExpressionVisitor)];
            }
        }

        return transformMapboxStyleFonts(style, fontVisitor, fontExpressionVisitor);
    });
}
